import { Box, Button, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useMemo } from 'react';

import { Invoice, InvoiceStages, EntryType } from 'openapi';

import { RejectDialog } from 'components/forms/InvoiceVerificationForms/RejectDialog';
import { ConfirmationDialog } from 'components/shared/Modal/ConfirmationDialog';

import { usePermissions } from 'context/PermissionsContext';
import { useTranslations } from 'context/TranslationContext';

import { useModal } from 'hooks/useModal';

import { DocumentConfig } from 'utils/config/documentEntries';

interface ApprovalButtonsProps {
  currentInvoice: Invoice;
  handleApprove: () => void;
  handleSettle: () => void;
  handleBack?: () => void;
  handleReject: (reason: string) => void;
  handleReturnToValidated: () => void;
  documentConfig: DocumentConfig;
}

export const ApprovalButtons = ({
  currentInvoice,
  handleBack,
  handleApprove,
  handleReject,
  handleReturnToValidated,
  handleSettle,
  documentConfig
}: ApprovalButtonsProps) => {
  const { translate } = useTranslations();
  const {
    isOpen: isRejectModalOpen,
    openModal: openRejectModal,
    closeModal: closeRejectModal
  } = useModal();
  const {
    isOpen: isSettleModalOpen,
    openModal: openSettleModal,
    closeModal: closeSettleModal
  } = useModal();
  const {
    isOpen: isReturnToValidatedModalOpen,
    openModal: openReturnToValidatedModal,
    closeModal: closeReturnToValidatedModal
  } = useModal();
  const { permissions } = usePermissions();
  const theme = useTheme();
  const isXsWidth = useMediaQuery(theme.breakpoints.down('sm'));

  const currentStage = currentInvoice.stage;

  // Invoice stage status checks - memoized to avoid repeated comparisons
  const isInvoiceApproved = useMemo(
    () => currentStage === InvoiceStages.APPROVED,
    [currentStage]
  );

  const isInvoiceRejected = useMemo(
    () => currentStage === InvoiceStages.REJECTED,
    [currentStage]
  );

  const isRevenueSettled = useMemo(
    () => currentStage === InvoiceStages.SETTLED,
    [currentStage]
  );

  const isDocumentInFinalState = useMemo(
    () => isInvoiceApproved || isInvoiceRejected || isRevenueSettled,
    [isInvoiceApproved, isInvoiceRejected, isRevenueSettled]
  );

  const isBackButtonDisabled = useMemo(
    () => isInvoiceApproved || isRevenueSettled,
    [isInvoiceApproved, isRevenueSettled]
  );

  const shouldShowReturnToDraft =
    currentStage === InvoiceStages.VALIDATED || isInvoiceRejected;

  return (
    <Box
      sx={{
        pt: 2.5,
        display: 'flex',
        justifyContent: handleBack ? 'space-between' : 'flex-end',
        alignItems: 'center'
      }}
    >
      {handleBack && currentStage !== InvoiceStages.FINALIZED && (
        <Tooltip
          title={
            isBackButtonDisabled
              ? translate('messages.approvalsBackButtonDisabled')
              : ''
          }
        >
          <span>
            <Button
              disabled={isBackButtonDisabled}
              onClick={handleBack}
              variant={shouldShowReturnToDraft ? 'contained' : 'outlined'}
              color={shouldShowReturnToDraft ? 'warning' : 'primary'}
            >
              {shouldShowReturnToDraft
                ? translate(
                    isXsWidth ? 'buttons.return' : 'labels.returnToDraft'
                  )
                : translate('buttons.back')}
            </Button>
          </span>
        </Tooltip>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        {currentStage === InvoiceStages.VALIDATED &&
          documentConfig.entryType === EntryType.REVENUE && (
            <Tooltip
              title={
                !permissions.INVOICE_SETTLEMENTS?.create &&
                translate('messages.unauthorizedToPerformAction')
              }
            >
              <span>
                <Button
                  onClick={openSettleModal}
                  disabled={!permissions.INVOICE_SETTLEMENTS?.create}
                  variant="contained"
                  color="primary"
                >
                  {translate('buttons.settle')}
                </Button>
              </span>
            </Tooltip>
          )}

        {currentStage === InvoiceStages.VALIDATED &&
          documentConfig.entryType === EntryType.EXPENSE && (
            <>
              <Tooltip
                title={
                  !currentInvoice.canRespond &&
                  translate('messages.notListedAsApproverInPendingGates')
                }
              >
                <span>
                  <Button
                    onClick={openRejectModal}
                    variant="contained"
                    disabled={!currentInvoice.canRespond}
                    color="primary"
                  >
                    {translate('buttons.reject')}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  !currentInvoice.canRespond &&
                  translate('messages.notListedAsApproverInPendingGates')
                }
              >
                <span>
                  <Button
                    onClick={() => handleApprove()}
                    variant="contained"
                    disabled={!currentInvoice.canRespond}
                    color="primary"
                  >
                    {translate('buttons.approve')}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}

        {isDocumentInFinalState && (
          <Tooltip
            title={
              !currentInvoice.canReturnToValidated &&
              translate('messages.unauthorizedToPerformAction')
            }
          >
            <span>
              <Button
                onClick={
                  isDocumentInFinalState
                    ? openReturnToValidatedModal
                    : openRejectModal
                }
                variant="contained"
                disabled={!currentInvoice.canReturnToValidated}
                color={isInvoiceRejected ? 'primary' : 'warning'}
              >
                {isInvoiceRejected
                  ? translate('buttons.returnForApproval')
                  : translate('buttons.returnToValidated')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
      <ConfirmationDialog
        isOpen={isReturnToValidatedModalOpen}
        onClose={closeReturnToValidatedModal}
        onConfirm={() => {
          handleReturnToValidated();
          closeReturnToValidatedModal();
        }}
        title={
          isInvoiceRejected
            ? translate('buttons.returnForApproval')
            : translate('buttons.returnToValidated')
        }
        size="sm"
      >
        {isInvoiceRejected
          ? translate('messages.returnRejectedInvoiceForApprovalConfirmation')
          : translate('messages.returnInvoiceConfirmation')}
      </ConfirmationDialog>
      <RejectDialog
        isOpen={isRejectModalOpen}
        onClose={closeRejectModal}
        onConfirm={(formData) => {
          handleReject(formData.rejectionReason);
          closeRejectModal();
        }}
        translate={translate}
      />
      <ConfirmationDialog
        isOpen={isSettleModalOpen}
        onClose={closeSettleModal}
        onConfirm={() => {
          handleSettle();
          closeSettleModal();
        }}
        title={translate('buttons.settle')}
        size="sm"
      >
        {translate('messages.settleRevenueConfirmation')}
      </ConfirmationDialog>
    </Box>
  );
};
